import { Elements } from '@stripe/react-stripe-js';
import { type BaseStripeElementsOptions } from '@stripe/stripe-js';
import React from 'react';

import { STRIPE_PROMISE } from '@/constants/stripe';

import { useColorScheme } from '@/hooks/useColorScheme';
import { webCampaign } from '@/modules/webCampaign';

// IMPORTANT: This is a TypeScript workaround for Stripe's type system
//
// The issue: We need to use 'paymentMethodCreation: "manual"' to get card funding information
// (including whether a card is prepaid) from Stripe. Without this prop, Stripe will throw
// an error saying we must provide paymentMethodCreation.
//
// The problem: TypeScript types only allow paymentMethodCreation when using 'mode' instead
// of 'clientSecret', but we need both clientSecret AND paymentMethodCreation for our flow.
//
// Solution: We extend the base type to include paymentMethodCreation, allowing us to use
// both properties together. This is safe because Stripe runtime accepts this combination.
type StripeElementsOptions = BaseStripeElementsOptions & {
  paymentMethodCreation?: 'manual';
};

export const StripeElements: React.FC<{
  children: React.ReactNode;
  clientSecret: string;
  stripeTheme?: 'flat';
}> = ({ children, clientSecret, stripeTheme }) => {
  const { theme } = useColorScheme();

  const getTheme = () => {
    if (theme === 'dark') {
      return webCampaign.getStaticTheme() === 'dark-violet' ? 'flat' : 'night';
    }
    return 'stripe';
  };

  const getAppearance = (): StripeElementsOptions['appearance'] => {
    if (stripeTheme === 'flat') {
      return {
        theme: getTheme(),
        labels: 'above',
        variables: {
          borderRadius: '12px',
          colorBackground:
            webCampaign.getStaticTheme() === 'dark-violet'
              ? '#62447E'
              : undefined,
          colorDanger: '#eb445a',
          colorText:
            webCampaign.getStaticTheme() === 'dark-violet' ? '#fff' : undefined,
          colorTextSecondary:
            webCampaign.getStaticTheme() === 'dark-violet'
              ? '#B29BC6'
              : undefined,
          colorPrimary:
            webCampaign.getStaticTheme() === 'dark-violet' ? '#fff' : undefined,
        },
      };
    }

    return {
      theme: theme === 'dark' ? 'night' : 'stripe',
      variables: {
        borderRadius: '12px',
        colorBackground:
          webCampaign.getStaticTheme() === 'dark-violet'
            ? '#2a1140'
            : undefined,
        colorDanger:
          webCampaign.getStaticTheme() === 'dark-violet'
            ? '#eb445a'
            : undefined,
      },
    };
  };

  return (
    <Elements
      stripe={STRIPE_PROMISE}
      options={
        {
          paymentMethodCreation: 'manual',
          clientSecret,
          appearance: {
            labels: 'above',
            ...getAppearance(),
          },
        } as StripeElementsOptions
      }
    >
      {children}
    </Elements>
  );
};
